// ── RefundCard ────────────────────────────────────────────────────────────────

import { ExternalLink } from "lucide-react";
import type { Transaction } from "@/application/money/types";
import { formatCurrency, formatAbsolute } from "@/application/money/types";
import { chargeUrl } from "@/lib/stripe-links";
import { CopyButton } from "./CopyButton";

interface RefundCardProps {
  transaction: Transaction;
}

export function RefundCard({ transaction: tx }: RefundCardProps) {
  if (tx.refundedAmount == null || tx.refundedAmount <= 0) return null;

  const isFull = tx.amount != null && tx.refundedAmount >= tx.amount;
  const exceedsFull = tx.amount != null && tx.refundedAmount > tx.amount;

  // Stripe doesn't have a dedicated refund URL; link to the parent charge page
  // (the refund appears inline there).
  const refundChargeLink = tx.sourceChargeId
    ? chargeUrl(tx.sourceChargeId, {
        chargeType: tx.chargeType,
        sourceAccount: tx.sourceAccount,
        currency: tx.currency,
      })
    : null;

  return (
    <div className="bg-card rounded-lg border border-l-4 border-l-yellow-400 p-5">
      <h2 className="mb-3 text-sm font-semibold text-yellow-700">
        {isFull ? "Full refund" : "Partial refund"}
      </h2>

      <div className="divide-y text-sm">
        <div className="flex justify-between py-2.5">
          <span className="text-muted-foreground">Refunded amount</span>
          <span className="text-foreground font-medium">
            {formatCurrency(tx.refundedAmount, tx.currency)}
            {exceedsFull && (
              <span className="ml-2 text-xs text-amber-600">
                (refund exceeds charge amount — tip included)
              </span>
            )}
          </span>
        </div>

        {tx.refunded != null && (
          <div className="flex justify-between py-2.5">
            <span className="text-muted-foreground">Refund date</span>
            <span className="text-foreground">
              {formatAbsolute(tx.refunded)}
            </span>
          </div>
        )}

        {tx.sourceRefundId && (
          <div className="flex justify-between py-2.5">
            <span className="text-muted-foreground">Refund ID</span>
            <span className="text-foreground flex items-center gap-1 font-mono text-xs">
              {tx.sourceRefundId}
              <CopyButton value={tx.sourceRefundId} />
              {refundChargeLink && (
                <a
                  href={refundChargeLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800"
                  aria-label="View refund in Stripe"
                >
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </span>
          </div>
        )}

        {tx.refundNote && (
          <div className="flex flex-col gap-1 py-2.5">
            <span className="text-muted-foreground">Note</span>
            <span className="text-foreground">{tx.refundNote}</span>
          </div>
        )}
      </div>
    </div>
  );
}
